import { makeAutoObservable, runInAction } from 'mobx';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';
import {
  agentChatPath,
  documentListLimit
} from '../../content/Mesh/sesam/constants';
import {
  DocumentItem,
  SearchResult,
  SesamMessage,
  ViewerDocument,
  ViewerTarget
} from '../../content/Mesh/sesam/types';
import {
  authHeaders,
  createMessageId,
  resolveSelectedDocument
} from '../../content/Mesh/sesam/utils';
import { redirectToLogin } from '../../utils/auth';

type ProjectsResponse = {
  projects?: string[];
  project_title?: string;
};

export interface SesamStore extends BaseStore {}

export class SesamStore {
  query = '';

  documents: DocumentItem[] = [];

  projects: string[] = [];

  selectedDocumentId?: number = undefined;

  thinking = false;

  error = '';

  viewerUrl = '';

  fileListOpen = true;

  viewerTarget?: ViewerTarget = undefined;

  messages: SesamMessage[] = [];

  modelMode: 'fast' | 'thinking' = 'fast';

  private activeStreamAbortController?: AbortController = undefined;

  private lastPrompt = '';

  private activeViewerObjectUrl = '';

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this, {
      activeStreamAbortController: false,
      activeViewerObjectUrl: false
    } as any);
  }

  get selectedDocument(): ViewerDocument | undefined {
    const document = resolveSelectedDocument(
      this.documents,
      this.messages,
      this.selectedDocumentId
    );

    if (!document || this.viewerTarget?.documentId !== document.id) {
      return document;
    }

    return {
      ...document,
      page_number: this.viewerTarget.page_number,
      slide_number: this.viewerTarget.slide_number,
      slide_id: this.viewerTarget.slide_id
    };
  }

  get canRetry() {
    return Boolean(this.lastPrompt);
  }

  setQuery(value: string) {
    this.query = value;
  }

  setModelMode(mode: 'fast' | 'thinking') {
    this.modelMode = mode;
  }

  async enhancePrompt(prompt: string) {
    const response = await this.request('/api/sharepoint/agent/enhance-prompt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, model_mode: this.modelMode })
    });
    const data = await response.json();
    return typeof data?.prompt === 'string' ? data.prompt : prompt;
  }

  toggleFileList() {
    this.fileListOpen = !this.fileListOpen;
  }

  async loadInitialData() {
    await Promise.all([this.loadDocuments(), this.loadProjects()]);
  }

  async loadSelectedDocumentForViewer() {
    const selectedDocument = this.selectedDocument;

    this.clearViewerObjectUrl();

    if (
      !selectedDocument ||
      !this.canPreviewInline(selectedDocument.mime_type)
    ) {
      runInAction(() => {
        this.viewerUrl = '';
      });
      return;
    }

    try {
      const response = await this.request(
        `/api/sharepoint/documents/${selectedDocument.id}/file`
      );
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      runInAction(() => {
        this.activeViewerObjectUrl = objectUrl;
        this.viewerUrl = this.createInlineViewerUrl(
          objectUrl,
          selectedDocument
        );
      });
    } catch {
      runInAction(() => {
        this.viewerUrl = '';
        this.error = 'Dokument konnte nicht im Viewer geladen werden.';
      });
    }
  }

  clearViewerObjectUrl() {
    if (this.activeViewerObjectUrl) {
      URL.revokeObjectURL(this.activeViewerObjectUrl);
      this.activeViewerObjectUrl = '';
    }
  }

  async loadDocuments() {
    runInAction(() => {
      this.error = '';
    });

    try {
      const response = await this.request(
        `/api/sharepoint/documents?limit=${documentListLimit}`
      );
      const documents = (await response.json()) as DocumentItem[];
      runInAction(() => {
        this.documents = documents;
      });
    } catch {
      runInAction(() => {
        this.error = 'Dokumente konnten nicht geladen werden.';
      });
    }
  }

  async loadProjects() {
    try {
      const response = await this.request('/api/sharepoint/projects');
      const data = (await response.json()) as ProjectsResponse;
      runInAction(() => {
        this.projects = this.toProjectList(data);
      });
    } catch {
      runInAction(() => {
        this.projects = [];
      });
    }
  }

  async createProject(projectTitle: string) {
    const response = await this.request('/api/sharepoint/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_title: projectTitle })
    });
    const data = (await response.json()) as ProjectsResponse;
    runInAction(() => {
      this.projects = this.toProjectList(data);
    });
    return data.project_title || projectTitle;
  }

  async deleteProject(projectTitle: string) {
    const response = await this.request(
      `/api/sharepoint/projects/${encodeURIComponent(projectTitle)}`,
      {
        method: 'DELETE'
      }
    );
    const data = (await response.json()) as ProjectsResponse;
    const selectedDocument = this.documents.find(
      (document) => document.id === this.selectedDocumentId
    );

    runInAction(() => {
      this.projects = this.toProjectList(data);
      this.documents = this.documents.filter(
        (document) => document.project_title !== projectTitle
      );

      if (selectedDocument?.project_title === projectTitle) {
        this.selectedDocumentId = undefined;
        this.viewerTarget = undefined;
        this.viewerUrl = '';
      }
    });

    await this.loadDocuments();
  }

  async uploadDocuments(projectTitle: string, files: File[]) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    await this.request(
      `/api/sharepoint/documents/${encodeURIComponent(projectTitle)}`,
      {
        method: 'POST',
        body: formData
      }
    );
    await this.loadProjects();
    await this.loadDocuments();
  }

  async updateDocumentMetadata(documentId: number, name: string) {
    const response = await this.request(
      `/api/sharepoint/documents/${documentId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      }
    );
    const updatedDocument = (await response.json()) as DocumentItem;

    runInAction(() => {
      this.documents = this.documents.map((document) =>
        document.id === documentId
          ? { ...document, ...updatedDocument }
          : document
      );
    });
    await this.loadProjects();
  }

  async deleteDocument(documentId: number) {
    await this.request(`/api/sharepoint/documents/${documentId}`, {
      method: 'DELETE'
    });

    runInAction(() => {
      this.documents = this.documents.filter(
        (document) => document.id !== documentId
      );

      if (this.selectedDocumentId === documentId) {
        this.selectedDocumentId = undefined;
        this.viewerTarget = undefined;
        this.viewerUrl = '';
      }
    });

    await this.loadProjects();
  }

  async sendMessage(promptOverride?: string) {
    const question = (promptOverride ?? this.query).trim();

    if (!question || this.thinking) {
      return;
    }

    this.lastPrompt = question;
    const assistantId = createMessageId('assistant');
    const abortController = new AbortController();
    this.activeStreamAbortController?.abort();
    this.activeStreamAbortController = abortController;
    const previousMessages = this.messages;

    runInAction(() => {
      this.messages = [
        ...this.messages,
        {
          id: createMessageId('user'),
          role: 'user',
          content: question
        },
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          status: 'Textsuche und Scope-Antwort werden vorbereitet.'
        }
      ];
      this.query = '';
      this.thinking = true;
      this.error = '';
    });

    try {
      const shouldSearchDocuments = !this.shouldSkipDocumentSearch(question);
      const sources = shouldSearchDocuments
        ? await this.searchDocuments(question, abortController.signal)
        : [];
      const streamMessages = [
        ...previousMessages
          .filter((message) => message.content.trim())
          .map((message) => ({
            role: message.role,
            content: message.content
          })),
        { role: 'user', content: question }
      ];

      this.updateAssistantMessage(assistantId, {
        status: !shouldSearchDocuments
          ? 'Dokumentenbestand wird geprüft. Scope-Antwort läuft.'
          : sources.length
          ? `${sources.length} Treffer gefunden. Scope-Antwort läuft.`
          : 'Keine direkten Treffer gefunden. Scope-Antwort läuft.'
      });

      await this.streamAgentAnswer(
        streamMessages,
        assistantId,
        abortController.signal
      );
    } catch (error) {
      if (this.isAbortError(error)) {
        this.updateAssistantMessage(assistantId, {
          content: 'Antwort abgebrochen.',
          status: undefined
        });
        return;
      }

      this.updateAssistantMessage(assistantId, {
        content: 'Die kombinierte Suche konnte nicht abgeschlossen werden.',
        status: undefined
      });
      runInAction(() => {
        this.error =
          'Textsuche und Scope-Antwort konnten nicht erstellt werden.';
      });
    } finally {
      if (this.activeStreamAbortController === abortController) {
        this.activeStreamAbortController = undefined;
      }
      runInAction(() => {
        this.thinking = false;
      });
    }
  }

  cancelAnswer() {
    this.activeStreamAbortController?.abort();
    this.activeStreamAbortController = undefined;
    this.thinking = false;
  }

  retryLastQuestion() {
    if (!this.lastPrompt || this.thinking) {
      return;
    }

    void this.sendMessage(this.lastPrompt);
  }

  selectDocument(
    documentId: number,
    target?: Omit<ViewerTarget, 'documentId'>
  ) {
    this.selectedDocumentId = documentId;
    this.viewerTarget = target ? { documentId, ...target } : undefined;
  }

  startNewChat() {
    if (this.thinking) {
      return;
    }

    this.messages = [];
    this.query = '';
    this.error = '';
  }

  private async request(path: string, options: RequestInit = {}) {
    const headers = new Headers(options.headers);

    Object.entries(authHeaders()).forEach(([key, value]) => {
      headers.set(key, value);
    });

    const response = await fetch(`${this.helper.getBaseUrl}${path}`, {
      ...options,
      headers
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 422) {
        redirectToLogin();
      }
      throw new Error(`Request failed: ${response.status}`);
    }

    return response;
  }

  private async searchDocuments(question: string, signal?: AbortSignal) {
    const response = await this.request(
      `/api/sharepoint/documents/search?q=${encodeURIComponent(
        question
      )}&limit=20`,
      { signal }
    );
    const results = (await response.json()) as SearchResult[];
    return results.map((source, index) =>
      this.normalizeSearchSource(source, index)
    );
  }

  private async streamAgentAnswer(
    agentMessages: Array<{ role: string; content: string }>,
    assistantId: string,
    signal?: AbortSignal
  ) {
    const response = await fetch(`${this.helper.getBaseUrl}${agentChatPath}`, {
      method: 'POST',
      headers: authHeaders('application/json'),
      signal,
      body: JSON.stringify({
        messages: agentMessages,
        web_access_enabled: false,
        model_mode: this.modelMode
      })
    });

    if (!response.ok || !response.body) {
      if (response.status === 401 || response.status === 422) {
        redirectToLogin();
      }
      throw new Error(`Request failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let pendingContent = '';
    let pendingSources: SearchResult[] | undefined;
    let flushTimer: number | undefined;

    const flushPendingContent = () => {
      if (flushTimer !== undefined) {
        window.clearTimeout(flushTimer);
        flushTimer = undefined;
      }

      if (!pendingContent && !pendingSources) {
        return;
      }

      const content = pendingContent;
      const sources = pendingSources;
      pendingContent = '';
      pendingSources = undefined;

      this.appendAssistantContent(assistantId, content, false, sources);
    };

    const scheduleFlush = () => {
      if (flushTimer !== undefined) {
        return;
      }

      flushTimer = window.setTimeout(flushPendingContent, 80);
    };

    const applyBufferedStreamEvent = (event: any) => {
      const hasSourcePayload = Array.isArray(event?.sources);
      const streamSources = this.normalizeAgentSources(event?.sources);

      if (
        (typeof event?.status === 'string' && event.status.trim()) ||
        (typeof event?.error === 'string' && event.error.trim()) ||
        Boolean(event?.replace) ||
        (hasSourcePayload && typeof event?.content !== 'string')
      ) {
        flushPendingContent();
        this.applyStreamEvent(assistantId, event);
        return;
      }

      if (typeof event?.content === 'string' && event.content) {
        pendingContent += event.content;

        if (streamSources?.length) {
          pendingSources = streamSources;
        }

        scheduleFlush();
        return;
      }

      if (hasSourcePayload) {
        flushPendingContent();
        this.applyStreamEvent(assistantId, event);
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        lines
          .filter((line) => line.trim())
          .map((line) => this.safeParseStreamEvent(line))
          .filter(Boolean)
          .forEach((event) => {
            applyBufferedStreamEvent(event);
          });
      }

      const remaining = buffer.trim();

      if (remaining) {
        const event = this.safeParseStreamEvent(remaining);

        if (event) {
          applyBufferedStreamEvent(event);
        }
      }
    } finally {
      flushPendingContent();
    }
  }

  private applyStreamEvent(assistantId: string, event: any) {
    const hasSourcePayload = Array.isArray(event?.sources);
    const streamSources = this.normalizeAgentSources(event?.sources);
    const messageSources = hasSourcePayload ? streamSources ?? [] : undefined;

    if (typeof event?.status === 'string' && event.status.trim()) {
      this.updateAssistantMessage(assistantId, { status: event.status });
    }

    if (typeof event?.error === 'string' && event.error.trim()) {
      this.appendAssistantContent(
        assistantId,
        `\n${event.error}`,
        false,
        messageSources
      );
      return;
    }

    if (typeof event?.content === 'string' && event.content) {
      this.appendAssistantContent(
        assistantId,
        event.content,
        Boolean(event.replace),
        messageSources
      );
      return;
    }

    if (hasSourcePayload) {
      this.updateAssistantMessage(assistantId, {
        sources: messageSources
      });
    }
  }

  private safeParseStreamEvent(line: string) {
    try {
      return JSON.parse(line);
    } catch {
      return undefined;
    }
  }

  private isAbortError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      String((error as { name?: string }).name) === 'AbortError'
    );
  }

  private normalizeSearchSource(
    source: SearchResult,
    index: number
  ): SearchResult {
    return {
      ...source,
      id: source.id ?? index + 1,
      type: 'file',
      title:
        source.title ||
        `${source.project_title || 'Ohne Kunde'} / ${source.name}`,
      file_id: source.file_id || String(source.document_id)
    };
  }

  private normalizeAgentSources(sources: unknown): SearchResult[] | undefined {
    if (!Array.isArray(sources)) {
      return undefined;
    }

    const normalizedSources = sources
      .map((source, index) => this.normalizeAgentSource(source, index))
      .filter((source): source is SearchResult => Boolean(source));

    return normalizedSources.length ? normalizedSources : undefined;
  }

  private normalizeAgentSource(
    source: unknown,
    index: number
  ): SearchResult | undefined {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    const candidate = source as Record<string, unknown>;

    if (candidate.type !== 'file') {
      return undefined;
    }

    const documentId = this.toNumber(
      candidate.document_id ?? candidate.file_id
    );

    if (!documentId) {
      return undefined;
    }

    const title = this.toStringValue(candidate.title);
    const name =
      this.toStringValue(candidate.name) ||
      this.sourceNameFromTitle(title) ||
      'Dokument';
    const projectTitle =
      this.toStringValue(candidate.project_title) ||
      this.sourceProjectFromTitle(title);

    return {
      id: this.toNumber(candidate.id) || index + 1,
      type: 'file',
      title: title || `${projectTitle || 'Ohne Kunde'} / ${name}`,
      file_id: this.toStringValue(candidate.file_id) || String(documentId),
      chunk_id: this.toNumber(candidate.chunk_id) || index + 1,
      document_id: documentId,
      project_title: projectTitle,
      page_number: this.toNumber(candidate.page_number),
      slide_number: this.toNumber(candidate.slide_number),
      slide_id: this.toNumber(candidate.slide_id),
      name,
      content: '',
      web_url: this.toStringValue(candidate.web_url ?? candidate.url),
      mime_type: this.toStringValue(candidate.mime_type),
      tags: Array.isArray(candidate.tags)
        ? candidate.tags.map((tag) => String(tag))
        : [],
      score: this.toNumber(candidate.score),
      created_at: this.toStringValue(candidate.created_at),
      last_modified_at: this.toStringValue(candidate.last_modified_at)
    };
  }

  private toNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toStringValue(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmed = value.trim();
    return trimmed || undefined;
  }

  private sourceNameFromTitle(title?: string) {
    if (!title) {
      return undefined;
    }

    return title.split('/').pop()?.trim();
  }

  private sourceProjectFromTitle(title?: string) {
    if (!title || !title.includes('/')) {
      return undefined;
    }

    return title.split('/')[0]?.trim();
  }

  private updateAssistantMessage(
    assistantId: string,
    patch: Partial<SesamMessage>
  ) {
    runInAction(() => {
      this.messages = this.messages.map((message) =>
        message.id === assistantId ? { ...message, ...patch } : message
      );
    });
  }

  private appendAssistantContent(
    assistantId: string,
    content: string,
    replace: boolean,
    sources?: SearchResult[]
  ) {
    runInAction(() => {
      this.messages = this.messages.map((message) =>
        message.id === assistantId
          ? {
              ...message,
              content: replace ? content : `${message.content}${content}`,
              sources: sources ?? message.sources,
              status: undefined
            }
          : message
      );
    });
  }

  private toProjectList(data: ProjectsResponse) {
    return Array.isArray(data.projects)
      ? data.projects.map((value) => String(value))
      : [];
  }

  private canPreviewInline(mimeType?: string) {
    return Boolean(
      mimeType &&
        (mimeType === 'application/pdf' || mimeType.startsWith('image/'))
    );
  }

  private createInlineViewerUrl(objectUrl: string, document: ViewerDocument) {
    if (document.mime_type !== 'application/pdf') {
      return objectUrl;
    }

    const params = new URLSearchParams({
      pagemode: 'none',
      toolbar: '1',
      view: 'FitH'
    });

    if (document.page_number) {
      params.set('page', String(document.page_number));
    }

    return `${objectUrl}#${params.toString()}`;
  }

  private shouldSkipDocumentSearch(question: string) {
    const text = question.toLowerCase();
    const documentTerms = [
      'datei',
      'dateien',
      'dokument',
      'dokumente',
      'unterlagen'
    ];
    const countTerms = [
      'anzahl',
      'wieviel',
      'wie viel',
      'wieviele',
      'wie viele',
      'count',
      'gesamt',
      'zugriff',
      'zugreifen'
    ];

    return (
      documentTerms.some((term) => text.includes(term)) &&
      countTerms.some((term) => text.includes(term))
    );
  }
}
